import React, { useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import * as Yup from 'yup'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import { phoneNumberRegex, phoneNumberMask, fullDateMask } from 'utils'
import MaskedInput from 'react-text-mask'
import { add } from 'date-fns'

import FormsStore from 'stores/forms'
import { useStore } from 'hooks'
import StateSelectField from 'components/StateSelectField'

const PersonalInfoSchema = Yup.object().shape({
  first_name: Yup.string().trim().min(1, 'First Name should be non-empty').required('Field is required'),
  last_name: Yup.string().trim().min(1, 'Last Name should be non-empty').required('Field is required'),
  birth_date: Yup.date()
    .max(add(new Date(), { years: -21 }), 'You must be over 21 years old to continue')
    .required('Field is required')
    .typeError('Must be a valid date'),
  gender: Yup.string().trim().min(1, 'Please select an option').required('Please Select an Option'),
  address_line_1: Yup.string().trim().min(1, 'Address should be non-empty').required('Field is required'),
  address_line_2: Yup.string().trim(),
  city: Yup.string().trim().min(1, 'City should be non-empty').required('Field is required'),
  state: Yup.string().trim().min(1, 'Please choose a state').required('Field is required'),
  zip_code: Yup.string().trim().min(1, 'Zip Code should not be empty').required('Field is required'),
  phone: Yup.string()
    .trim()
    .min(1, 'Phone number should not be empty')
    .matches(phoneNumberRegex, 'Phone number is not valid')
    .required('Field is required')
})

const PersonalInfoForm = () => {
  const router = useRouter()
  const formsStore = useStore(FormsStore)
  const [continueBlocked, setContinueBlocked] = useState(true)
  const handleSubmit = useCallback(
    ({ first_name, last_name, birth_date, gender, address_line_1, address_line_2, city, state, zip_code, phone }) => {
      // add info to the forms store somehow
      formsStore.editSubmissionInProgress({ name: `${first_name} ${last_name}` })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'First Name',
        answer: first_name
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'Last Name',
        answer: last_name
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'Birth Date',
        answer: birth_date
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'Gender',
        answer: gender
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'Address Line 1',
        answer: address_line_1
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'Address Line 2',
        answer: address_line_2
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'City',
        answer: city
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'State',
        answer: state
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'Zip Code',
        answer: zip_code
      })
      formsStore.addFieldToSubmissionInProgress({
        prompt: 'Phone Number',
        answer: phone
      })

      router.push('/barstool-sportsbook/wager-frequency')
    },
    [formsStore, router]
  )
  const checkIfValid = async (values) => {
    try {
      const result = await PersonalInfoSchema.validate(values)
      if (result.state === 'non-us' && !continueBlocked) {
        // if continue isn't blocked, block it because of the out of state value
        setContinueBlocked(true)
      } else if (continueBlocked && result.state !== 'non-us') {
        // otherwise unblock it if it isn't already because it's valid
        setContinueBlocked(false)
      }
    } catch {
      // if continue isn't blocked already you should block it because of validation error
      if (!continueBlocked) setContinueBlocked(true)
    }
  }
  return (
    <>
      <Formik
        initialValues={{
          first_name: '',
          last_name: '',
          birth_date: '',
          gender: '',
          address_line_1: '',
          address_line_2: '',
          city: '',
          state: '',
          zip_code: '',
          phone: ''
        }}
        validationSchema={PersonalInfoSchema}
        validateOnBlur
        validateOnChange
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values }) => {
          checkIfValid(values)
          return (
            <Form className='sportsBookForm'>
              <Field name='first_name' placeholder='FIRST NAME' className='sportsBook__formField' />
              <ErrorMessage name='first_name' component='div' className='field__error' />

              <Field name='last_name' placeholder='LAST NAME' className='sportsBook__formField' />
              <ErrorMessage name='last_name' component='div' className='field__error' />

              <Field name='birth_date' placeholder='DATE OF BIRTH [MM/DD/YYYY]' className='sportsBook__formField'>
                {({ field }) => (
                  <MaskedInput
                    {...field}
                    type='tel'
                    mask={fullDateMask}
                    placeholder='DATE OF BIRTH [MM/DD/YYYY]'
                    className='sportsBook__formField'
                  />
                )}
              </Field>
              <ErrorMessage name='birth_date' component='div' className='field__error' />

              <div className='dropdown'>
                <Field name='gender' placeholder='GENDER'>
                  {({ field }) => (
                    <select {...field} className='sportsBook__formField'>
                      <option value='' disabled selected>
                        GENDER
                      </option>
                      <option value='male'>MALE</option>
                      <option value='female'>FEMALE</option>
                      <option value='anon'>PREFER NOT TO SAY</option>
                    </select>
                  )}
                </Field>
              </div>
              <ErrorMessage name='gender' component='div' className='field__error' />

              <Field name='address_line_1' placeholder='ADDRESS LINE 1' className='sportsBook__formField' />
              <ErrorMessage name='address_line_1' component='div' className='field__error' />

              <Field name='address_line_2' placeholder='ADDRESS LINE 2' className='sportsBook__formField' />
              <ErrorMessage name='address_line_2' component='div' className='field__error' />

              <Field name='city' placeholder='CITY' className='sportsBook__formField' />
              <ErrorMessage name='city' component='div' className='field__error' />

              <Field name='state' placeholder='STATE' className='sportsBook__formField'>
                {({ field }) => (
                  <StateSelectField
                    {...field}
                    onChange={(value) => setFieldValue('state', value)}
                    displayErrorForNonUS
                    nonStatesErrorMessage='Sorry, Barstool Sportsbook is not available in your area.'
                    placeholder='Choose a State'
                    className='sportsBook__formField'
                  />
                )}
              </Field>
              <ErrorMessage name='state' component='div' className='field__error' />

              <Field name='zip_code' type='tel' placeholder='ZIP CODE' className='sportsBook__formField' />
              <ErrorMessage name='zip_code' component='div' className='field__error' />

              <Field name='phone' type='tel' placeholder='PHONE NUMBER' className='sportsBook__formField'>
                {({ field }) => (
                  <MaskedInput
                    {...field}
                    mask={phoneNumberMask}
                    placeholder='PHONE NUMBER'
                    type='tel'
                    className='sportsBook__formField'
                  />
                )}
              </Field>
              <ErrorMessage name='phone' component='div' className='field__error' />

              <button
                type='submit'
                className={`sportsBook__submitButton${continueBlocked ? '--disabled' : ''}`}
                disabled={continueBlocked}
              >
                Continue
              </button>
            </Form>
          )
        }}
      </Formik>
      <style jsx>{`
        .dropdown {
          position: relative;
        }
        .dropdown:after {
          background-image: url('/static/images/sportsbook/chevron.svg');
          background-size: 16px 16px;
          content: '   ';
          font: 17px 'Helvetica Neue';
          height: 16px;
          width: 16px;
          color: #757575;
          right: 11px;
          top: 14px;
          padding: 0 0 2px;
          position: absolute;
          pointer-events: none;
        }
      `}</style>
    </>
  )
}

export default PersonalInfoForm
